// src/layouts/documents/documentTableData.js

import React from "react";
import PropTypes from "prop-types";
import Chip from "@mui/material/Chip";
import MDTypography from "components/MDTypography";
import { Link } from "react-router-dom";
import { useLanguage } from "context/LanguageContext";
import { getDocumentTypeMeta } from "common/utils/documentType";
import { IconButton, Tooltip, Box } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import InfoIcon from "@mui/icons-material/Info";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

// Komponentas dokumento pavadinimui atvaizduoti - modified to be more compact
const Document = ({ title, isValid, totalsMismatch, lineItemsMismatch, t }) => {
  const getTooltip = () => {
    if (lineItemsMismatch) return t("lineItemsMismatch") || "Eilučių suma nesutampa su suma be PVM";
    if (totalsMismatch) return t("validationTotalsMismatch") || "Sumos tarpusavyje nesutampa";
    return t("documentRequiresReview") || "Dokumentui reikalingas patikrinimas";
  };

  return (
    <Box display="flex" alignItems="center" gap={0.75}>
      {isValid === false && (
        <Tooltip title={getTooltip()}>
          <WarningAmberIcon color="warning" sx={{ fontSize: "1.2rem", flexShrink: 0 }} />
        </Tooltip>
      )}
      <MDTypography
        display="block"
        variant="button"
        fontWeight="medium"
        sx={{ fontSize: "0.875rem", lineHeight: 1.35, wordBreak: "break-word" }}
      >
        {title}
      </MDTypography>
    </Box>
  );
};

Document.propTypes = {
  title: PropTypes.string.isRequired,
  isValid: PropTypes.bool,
  totalsMismatch: PropTypes.bool,
  lineItemsMismatch: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

// Komponentas pavadinimo ląstelės renderinimui
const TitleCell = ({ row, value }) => {
  const { t } = useLanguage();
  return (
    <Document
      title={value}
      isValid={row?.original?.isValid}
      totalsMismatch={row?.original?.totalsMismatch}
      lineItemsMismatch={row?.original?.lineItemsMismatch}
      t={t}
    />
  );
};

TitleCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.object,
  }),
  value: PropTypes.string.isRequired,
};

// Naujas komponentas "Recognized" ląstelei, rodantis tik "Yes" arba "No"
const RecognizedTextCell = ({ value }) => {
  const { t } = useLanguage();

  return (
    <MDTypography variant="body2" sx={{ fontSize: "0.875rem", lineHeight: 1.35 }}>
      {value ? t("yes") : t("no")}
    </MDTypography>
  );
};

RecognizedTextCell.propTypes = {
  value: PropTypes.bool.isRequired, // Arba PropTypes.string jei reikia
};

const ExportStatusCell = ({ value }) => {
  const { t } = useLanguage();
  if (!value) return null;
  return (
    <Tooltip title={`${t("lastExported")}: ${new Date(value).toLocaleString()}`}>
      <Chip label={t("exported")} color="success" size="small" variant="outlined" />
    </Tooltip>
  );
};

ExportStatusCell.propTypes = {
  value: PropTypes.string,
};

// Funkcija, kuri grąžina duomenų struktūrą
export default function documentTableData(t, openDetailsDialog, onViewDocument) {
  const ActionsCell = ({ row }) => {
    const documentId = row.original.id;
    return (
      <Box display="flex" gap={1} onClick={(e) => e.stopPropagation()}>
        <Tooltip title={t("details") || "Details"}>
          <IconButton
            size="small"
            color="secondary"
            onClick={(e) => {
              e.stopPropagation();
              if (openDetailsDialog) openDetailsDialog(row.original);
            }}
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("view") || "View"}>
          <IconButton
            size="small"
            color="info"
            component={Link}
            to={`/documents/${documentId}`}
            onClick={() => onViewDocument?.(row.original)}
          >
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  ActionsCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object.isRequired,
    }).isRequired,
  };
  const ReverseChargeVat96Cell = ({ row }) => {
    if (!row?.original?.data?.is_reverse_charge_vat_96) return null;
    return (
      <Tooltip title="PVM 96 str.">
        <Chip
          label="96 str."
          color="warning"
          variant="outlined"
          size="small"
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
    );
  };

  ReverseChargeVat96Cell.propTypes = {
    row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  };

  const DocumentTypeCell = ({ row }) => {
    const meta = getDocumentTypeMeta(row?.original, t);
    if (!meta) return null;

    return (
      <Chip
        label={meta.label}
        color={meta.chipColor}
        variant={meta.chipVariant}
        size="small"
        sx={{ fontWeight: 500 }}
      />
    );
  };

  DocumentTypeCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object,
    }).isRequired,
  };

  const NetAmountCell = ({ row, value }) => {
    const isMismatch = !!row?.original?.lineItemsMismatch;
    return (
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
        {isMismatch && (
          <Tooltip title={t("lineItemsMismatch") || "Eilučių suma nesutampa su suma be PVM"}>
            <WarningAmberIcon color="warning" sx={{ fontSize: "1.1rem", cursor: "help" }} />
          </Tooltip>
        )}
        <MDTypography
          variant="caption"
          fontWeight={isMismatch ? 700 : 500}
          color={isMismatch ? "warning" : "text"}
        >
          {value || "-"}
        </MDTypography>
      </Box>
    );
  };

  NetAmountCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object,
    }),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  const TotalAmountCell = ({ row, value }) => {
    const isMismatch = !!row?.original?.totalsMismatch;
    return (
      <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
        {isMismatch && (
          <Tooltip title={t("validationTotalsMismatch") || "Sumos tarpusavyje nesutampa"}>
            <WarningAmberIcon color="warning" sx={{ fontSize: "1.1rem", cursor: "help" }} />
          </Tooltip>
        )}
        <MDTypography
          variant="caption"
          fontWeight={isMismatch ? 700 : 500}
          color={isMismatch ? "warning" : "text"}
        >
          {value || "-"}
        </MDTypography>
      </Box>
    );
  };

  TotalAmountCell.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.object,
    }),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  };

  return {
    columns: [
      {
        Header: t("title"),
        accessor: "title",
        Cell: TitleCell,
        width: 180, // Set a fixed width for the column
      },
      {
        Header: t("exportStatusShort"),
        accessor: "last_exported_at",
        disableSortBy: true,
        width: 76,
        Cell: ExportStatusCell,
      },
      {
        Header: t("rec"), // Changed from "Recognized" to "Rec"
        accessor: "is_recognized",
        Cell: RecognizedTextCell,
        width: 60, // Reduced width since header is shorter
      },
      {
        Header: t("documentType"),
        accessor: "document_type",
        Cell: DocumentTypeCell,
        disableSortBy: true,
        width: 160,
      },
      {
        Header: "PVM",
        accessor: "data.is_reverse_charge_vat_96",
        Cell: ReverseChargeVat96Cell,
        disableSortBy: true,
        width: 48,
      },
      {
        Header: t("supplierName"),
        accessor: "supplier_name",
      },
      {
        Header: t("supplierRegistration"),
        accessor: "supplier_registration",
      },
      {
        Header: t("receiverName"),
        accessor: "receiver_name",
      },
      {
        Header: t("receiverRegistration"),
        accessor: "receiver_registration",
      },
      {
        Header: t("invoiceId"),
        accessor: "invoice_id",
      },
      {
        Header: t("invoiceDate"),
        accessor: "invoice_date",
      },
      {
        Header: t("dueDate"),
        accessor: "due_date",
      },
      {
        Header: t("netAmount"),
        accessor: "net_amount",
        Cell: NetAmountCell,
      },
      {
        Header: t("taxAmount"),
        accessor: "total_tax_amount",
      },
      {
        Header: t("totalAmount"),
        accessor: "total_amount",
        Cell: TotalAmountCell,
      },
      {
        Header: t("currency"),
        accessor: "currency",
      },
      {
        Header: t("supplierAddress"),
        accessor: "supplier_address",
      },
      {
        Header: t("supplierIban"),
        accessor: "supplier_iban",
      },
      {
        Header: t("notes"),
        accessor: "notes",
      },
      {
        Header: t("actions"),
        accessor: "id",
        Cell: ActionsCell,
        disableSortBy: true,
      },
    ],
  };
}
